import { HStack, Text, VStack } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import {
  PatientServicePlanSignatureClaimStatus,
  PatientServicePlanSignatureRole,
  PatientServicePlanStatus,
} from "../../../shared/schema/gql/graphql";
import { dateFormatter } from "../../../shared/utils/date-formatter";
import { GET_SERVICE_PLANS_QUERY } from "../service-plan.graphql";
import {
  ServicePlanRow,
  ServicePlanSignatureClaimRow,
} from "../types/service-plan-dashboard.types";
import ClaimSignatureByEmailButton from "./ClaimSignatureByEmailButton";

export const SIGNATURE_ROLE_TITLES: Record<PatientServicePlanSignatureRole, string> = {
  [PatientServicePlanSignatureRole.Assessor]: "Assessor",
  [PatientServicePlanSignatureRole.Consumer]: "Consumer",
  [PatientServicePlanSignatureRole.CaseManager]: "Case manager",
};

interface Props {
  plan: ServicePlanRow;
}

/**
 * The live signing links of a plan: who each went to, its status, and whether it
 * is still usable. Roles with no link yet are not listed — sending a first link
 * lives in the plan editor's Signatures step.
 */
export default function ServicePlanSignatureClaimsCell({ plan }: Props) {
  const queryClient = useQueryClient();

  const claims = plan.signatureClaims.nodes;

  if (claims.length === 0) {
    return <Text color="gray.500">—</Text>;
  }

  const invalidateList = () =>
    queryClient.invalidateQueries({ queryKey: [GET_SERVICE_PLANS_QUERY] });

  return (
    <VStack align="start" spacing={3}>
      {claims.map((claim) => (
        <VStack key={claim.id} align="start" spacing={0}>
          <Text fontSize="sm" fontWeight="medium" whiteSpace="nowrap">
            {SIGNATURE_ROLE_TITLES[claim.role]}
          </Text>
          <Text color="gray.600" fontSize="xs">
            {claim.recipientEmail}
          </Text>
          <HStack paddingY={1} spacing={2}>
            <ClaimSignatureByEmailButton
              claim={claim}
              isDisabled={plan.status === PatientServicePlanStatus.Finalized}
              role={claim.role}
              servicePlanId={plan.id}
              title={SIGNATURE_ROLE_TITLES[claim.role]}
              onSent={invalidateList}
            />
          </HStack>
          <ClaimValidity claim={claim} />
        </VStack>
      ))}
    </VStack>
  );
}

function ClaimValidity(props: { claim: ServicePlanSignatureClaimRow }) {
  const { claim } = props;

  switch (claim.status) {
    case PatientServicePlanSignatureClaimStatus.Claimed:
      return (
        <Text color="green.600" fontSize="xs">
          signed
        </Text>
      );
    case PatientServicePlanSignatureClaimStatus.Expired:
      return (
        <Text color="red.500" fontSize="xs">
          link expired {dateFormatter.toDate(claim.expiresAt)}
        </Text>
      );
    case PatientServicePlanSignatureClaimStatus.Awaiting:
    case PatientServicePlanSignatureClaimStatus.Opened:
      return (
        <Text color="gray.600" fontSize="xs">
          link valid until {dateFormatter.toDate(claim.expiresAt)}
        </Text>
      );
  }
}
